/* ============
 * Reports Page
 * ============
 *
 * This is the page for reports.
 */
import moment from 'moment';
import reportService from './../../../services/report';
import AggregatedSalesReportTransformer from './../../../transformers/aggregatedSalesReport';
import orderTypeService from './../../../services/orderType';
import helpers from './../../../utils/helpers';
import warehouseService from './../../../services/warehouse';
import accountService from './../../../services/account';
import transactionTypeService from './../../../services/transactionType';
import { mapGetters, mapMutations } from 'vuex';
import debounce from 'debounce';
import bulkProcessTurnOverModal from './../../../components/bulkProcessTurnOverModal/bulkProcessTurnOverModal.vue';

export default {
  data() {
    return {
      clientSearch: '',
      tabIndex: 'clients',
      greenDotSelectedYear: '',
      clientData: [],
      client: '',
      client_id: null,
      warehouse: '',
      warehouse_id: null,
      transaction_type_id: '',
      transactionDateRange: [],
      transactionMonth: null,
      groupByProduct: true,
      loadingClients: false,
      salesData: [],
      loadingSales: false,
      exciseLoading: false,
      salesDateRange: [moment().subtract(1, 'months'), moment()],
      salesGrouping: '',
      salesType: '',
      salesFilter: '',
      salesGroupingOptions: [
        {
          name: this.$t('static.reports.sales.group.names.orders'),
          id: '',
        },
        {
          name: this.$t('static.reports.sales.group.names.clients'),
          id: 'clients',
        },
        {
          name: this.$t('static.reports.sales.group.names.products'),
          id: 'products',
        },
      ],
      loadingBills: false,
      billData: [],
      billsDateRange: [moment().subtract(1, 'months'), moment()],
      billClientId: '',
      loadingFees: false,
      feeData: [],
      warehouseData: [],
      warehouseTransactionData: [],
      feesDateRange: [moment().subtract(1, 'months'), moment()],
      feesMonth: '',
      feeReportLoaded: false,
      exciseReport: [],
      helsedirReport: [],
      infinitumReport: [],
      exciseDateRange: moment(),
      loadingExcise: false,
      warehouseLoading: false,
      limit: 25,
      yearList: [],
      selectedYear: '',
      multipleSelection: [],
      buttonLoading: [],
      buttonBulkSendLoading: false,
      buttonBulkDownloadLoading: false,
      disableBulk: true,
      dropdownState: [],
      dateOption: 'as_of_date',
      asOfDate: '',
      hideOutOfStock: true,
      // Aggregated sales report
      loadingAggSales: false,
      aggSalesData: [],
      aggSalesSummary: null,
      aggSalesColumns: [],
      aggSalesDateRange: [moment().subtract(1, 'months'), moment()],
      aggSalesGroupBy: ['product'],
      aggSalesSort: 'revenue_desc',
      aggSalesIncludeCancelled: false,
      aggSalesStatuses: [],
      aggSalesMetrics: ['totalRevenueNet', 'totalRevenueGross', 'totalVolumeLiters', 'orderCount', 'totalQuantityUnits'],
    };
  },

  computed: {
    ...mapGetters({
      warehouses: 'allWarehouses',
      account: 'account',
      excisePeriods: 'paginatedExcisePeriods',
      transactionTypes: 'allTransactionTypes',
      orderTypes: 'orderTypes',
    }),
    clientList() {
      return this.clientData.map(c => ({
        id: c.id,
        name: c.name,
        clientNumber: c.clientNumber,
        balance: Number(c.balance.toFixed(0)),
        createdAt: c.createdAt,
      }));
    },

    salesList() {
      return this.salesData.map((r) => {
        r.total = r.clearedInvoices - r.clearedCredit;
        if (this.salesGrouping !== '') {
          delete r.status;
        }
        return r;
      });
    },

    billList() {
      return this.billData.map((r) => {
        delete r.address;
        delete r.email;
        delete r.invoiceDate;
        delete r.vat;
        delete r.sum;

        return r;
      });
    },

    feeList() {
      return this.feeData.map(r => r);
    },

    warehouseList() {
      return this.warehouseData.map(({ vmpNumber, ...fields }) => fields);
    },

    transactionListNew() {
      if (this.warehouseTransactionData.length === 0) {
        return false;
      }
      return this.warehouseTransactionData.map(c => ({
        id: c.parentModelId,
        key: `${c.via} ${c.code}`,
        productName: c.productName,
        via: `${c.via} #${c.code}`,
        warehouse: c.warehouse,
        beforeqty: c.beforeQty,
        qty: c.adjustmentQty,
        afterqty: c.afterQty,
        createdAt: c.createdAt,
      }));
    },

    hasExcise() {
      let hasModule = false;
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name === 'Excise') {
            hasModule = true;
          }
        }
      }
      return hasModule;
    },

    salesTypeOptions() {
      const salesTypes = this.orderTypes.map(type => ({
        id: type.id,
        name: this.$t(`static.orders.types.${type.name.toLowerCase()}`),
      }));
      salesTypes.unshift({
        id: '',
        name: this.$t('static.orders.types.all'),
      });
      return salesTypes;
    },

    showAsOfDate() {
      return this.dateOption === 'as_of_date';
    },

    showDateRange() {
      return this.dateOption === 'date_range';
    },

    aggSalesGroupByOptions() {
      return [
        { id: 'product',     name: this.$t('static.reports.aggregatedSales.groupBy.product') },
        { id: 'client',      name: this.$t('static.reports.aggregatedSales.groupBy.client') },
        { id: 'client_type', name: this.$t('static.reports.aggregatedSales.groupBy.clientType') },
        { id: 'month',       name: this.$t('static.reports.aggregatedSales.groupBy.month') },
        { id: 'year',        name: this.$t('static.reports.aggregatedSales.groupBy.year') },
      ];
    },

    aggSalesSortOptions() {
      return [
        { id: 'revenue_desc', name: this.$t('static.reports.aggregatedSales.sort.revenueDesc') },
        { id: 'revenue_asc',  name: this.$t('static.reports.aggregatedSales.sort.revenueAsc') },
        { id: 'volume_desc',  name: this.$t('static.reports.aggregatedSales.sort.volumeDesc') },
        { id: 'volume_asc',   name: this.$t('static.reports.aggregatedSales.sort.volumeAsc') },
      ];
    },

    aggSalesStatusOptions() {
      const keys = ['confirmed', 'shipped', 'delivered', 'invoiced', 'created', 'cancelled'];
      return keys.map(k => ({
        id: k,
        name: this.$t(`static.reports.aggregatedSales.statuses.options.${k}`),
      }));
    },

    aggSalesDynamicColumns() {
      const groupKeys = this.aggSalesGroupBy || [];
      const cols = [];
      if (groupKeys.includes('product')) {
        cols.push({ key: 'product_name', prop: 'productName', label: this.$t('static.reports.aggregatedSales.columns.productName'), minWidth: 180 });
        cols.push({ key: 'product_number', prop: 'productNumber', label: this.$t('static.reports.aggregatedSales.columns.productNumber'), minWidth: 110 });
      }
      if (groupKeys.includes('client')) {
        cols.push({ key: 'client_name', prop: 'clientName', label: this.$t('static.reports.aggregatedSales.columns.clientName'), minWidth: 180 });
        cols.push({ key: 'client_number', prop: 'clientNumber', label: this.$t('static.reports.aggregatedSales.columns.clientNumber'), minWidth: 100 });
      }
      if (groupKeys.includes('client_type')) {
        cols.push({ key: 'client_type_name', prop: 'clientTypeName', label: this.$t('static.reports.aggregatedSales.columns.clientTypeName'), minWidth: 180 });
      }
      if (groupKeys.includes('month') || groupKeys.includes('year')) {
        cols.push({ key: 'period', prop: 'period', label: this.$t('static.reports.aggregatedSales.columns.period'), minWidth: 90 });
      }
      return cols;
    },

    aggSalesMetricOptions() {
      return [
        { id: 'totalRevenueNet', name: this.$t('static.reports.aggregatedSales.columns.totalRevenueNet') },
        { id: 'totalRevenueGross', name: this.$t('static.reports.aggregatedSales.columns.totalRevenueGross') },
        { id: 'totalVolumeLiters', name: this.$t('static.reports.aggregatedSales.columns.totalVolumeLiters') },
        { id: 'orderCount', name: this.$t('static.reports.aggregatedSales.columns.orderCount') },
        { id: 'totalQuantityUnits', name: this.$t('static.reports.aggregatedSales.columns.totalQuantityUnits') },
      ];
    },

  },

  mounted() {
    this.urlCurrentTab();
    this.assignTab(this.tabIndex);
  },

  created() {
    const self = this;
    window.addEventListener('click', (e) => {
      if (helpers.closeDropdown(e, self.dropdownState) === false) {
        self.dropdownState = false;
      }
    });

    accountService
      .rememberPerPage('get', { from: 'report_excise_entry' })
      .then((data) => {
        this.limit = data.size;
        this.showExcise(1, this.limit);
      })
      .catch((error) => {
        console.log(error);
      });

    this.initYearFilter();
    this.showClients();
    this.showBills();
    this.showSales();
    // this.showFees();

    this.showWarehouses();
    if (this.$route.query.t) {
      this.tabIndex = this.$route.query.t;
    }
    Promise.all([
      warehouseService.all(),
      transactionTypeService.all(),
      orderTypeService.all(),
    ]).then(() => {});
  },
  watch: {
    clientSearch(value) {
      this.showClients({ clientSearch: value });
    },

    client(client) {
      this.client_id = null;
    },

    selectedYear(value) {
      this.loadingClients = true;
      reportService.clients({ year: value }).then((clients) => {
        this.loadingClients = false;
        this.clientData = clients;
      });
    },

    multipleSelection(value) {
      if (value.length === 0) {
        this.disableBulk = true;
      } else {
        this.disableBulk = false;
      }
    },

    $route() {
      this.urlCurrentTab();
    },

    tabIndex(value) {
      this.assignTab(value);
    },
  },
  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
    }),

    assignTab(value) {
      let tab = '';
      if (value === 'excise') {
        tab = '#excise';
      } else if (value === 'warehouse') {
        tab = '#warehouse';
      } else if (value === 'bills') {
        tab = '#bills';
      } else if (value === 'sales') {
        tab = '#sales';
      } else if (value === 'greenDot') {
        tab = '#greenDot';
      } else if (value === 'aggregatedSales') {
        tab = '#aggregatedSales';
      } else {
        tab = '#clients';
      }
      history.pushState({}, null, `/#${this.$route.path}${tab}`);
    },

    urlCurrentTab() {
      if (this.$router.currentRoute.hash === '#excise') {
        this.tabIndex = 'excise';
      } else if (this.$router.currentRoute.hash === '#warehouse') {
        this.tabIndex = 'warehouse';
      } else if (this.$router.currentRoute.hash === '#bills') {
        this.tabIndex = 'bills';
      } else if (this.$router.currentRoute.hash === '#sales') {
        this.tabIndex = 'sales';
      } else if (this.$router.currentRoute.hash === '#greenDot') {
        this.tabIndex = 'greenDot';
      } else if (this.$router.currentRoute.hash === '#aggregatedSales') {
        this.tabIndex = 'aggregatedSales';
      } else {
        this.tabIndex = 'clients';
      }
    },

    generateGreenDotReport() {
      if (this.greenDotSelectedYear !== '') {
        window.open(helpers.buildUrl(`${process.env.API_LOCATION}/reports/greendot/export`, {
          year: this.greenDotSelectedYear,
          token: localStorage.getItem('id_token'),
        }));
      }
    },

    generateGreenDotDetailedReport() {
      if (this.greenDotSelectedYear !== '') {
        window.open(helpers.buildUrl(`${process.env.API_LOCATION}/reports/greendot/export/detailed`, {
          year: this.greenDotSelectedYear,
          token: localStorage.getItem('id_token'),
        }));
      }
    },

    selectable(row, index) {
      return true; // isHidden 0-no 1-yes return false is not optional
    },

    toggleDropdown(value) {
      this.dropdownState.splice(value.id, 1, !this.dropdownState[value.id]);
    },

    sendSelectedClient() {
      this.previewModalSend(this.multipleSelection);
    },

    sendClient(value) {
      this.previewModalSend([value.id]);
    },

    previewModalSend(selected) {
      const selectedClients = this.clientData.filter(item =>
        selected.some(item2 => item2 === item.id)
      );
      this.setModalContent(() => bulkProcessTurnOverModal);
      this.setModalProps({
        selected: selectedClients,
        emailData: {
          from: { id: this.account.email, name: this.account.organizationName },
          subject: `Omsetningsoppgave ${this.selectedYear}`,
          message: `Se vedlegg for omsetningsoppgave for ${this.selectedYear}.`,
          selectedYear: this.selectedYear,
        },
        // onSubmit: this.handleBulkSendActionSubmit(selected),
      });
      this.setModalCloseOnClick(false);
      this.setModalShowClose(false);
      this.setModalCloseOnPressEscape(false);
      this.toggleModal(true);
    },

    downloadSelectedClient() {
      this.buttonBulkDownloadLoading = true;
      reportService
        .downloadTurnOverPdf('validate', { ids: this.multipleSelection, selectedYear: this.selectedYear })
        .then((response) => {
          if (response.data.noData.length > 0) {
            this.setModalContent(() => bulkProcessTurnOverModal);
            this.setModalProps({
              selected: [],
              emailData: {},
              noData: response.data.noData,
              noDataAllSelected: response.data.noDataAllSelected,
              period: this.selectedYear.toString(),
            });
            this.setModalCloseOnClick(false);
            this.setModalShowClose(false);
            this.setModalCloseOnPressEscape(false);
            this.toggleModal(true);
          }
          if (!response.data.noDataAllSelected) {
            this.proceedProceedDownload();
          } else {
            this.buttonBulkDownloadLoading = false;
          }
        })
        .catch(() => {
          this.buttonBulkDownloadLoading = false;
        });
    },

    proceedProceedDownload() {
      reportService
        .downloadTurnOverPdf('multiple', {
          selectedYear: this.selectedYear,
          ids: this.multipleSelection,
          downloadTurnOver: true,
        })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: 'application/octet-stream',
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = 'omsetningsrapport-bulk.zip';
          link.click();
          this.buttonBulkDownloadLoading = false;
        })
        .catch(() => {
          this.buttonBulkDownloadLoading = false;
        });
    },

    viewTurnoverReport(value) {
      this.buttonLoading.splice(value.id, 1, true);
      reportService
        .downloadTurnOverPdf('single', {
          selectedYear: this.selectedYear,
          id: value.id,
          download: false,
        })
        .then((response) => {
          const file = new Blob([response.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
          this.buttonLoading.splice(value.id, 1, false);
        })
        .catch(() => {
          this.buttonLoading.splice(value.id, 1, false);
        });
    },

    handleSelectionChange(value) {
      this.multipleSelection = value.map(item => item.id);
    },

    initYearFilter() {
      reportService.exciseCalendar().then((response) => {
        let result = response.data[0].map(item =>
          new Date(item).getFullYear()
        );
        result = [...new Set(result)];
        result = result.sort((a, b) => b - a);
        this.yearList = result;
        const defaultYear = new Date();
        defaultYear.setFullYear(defaultYear.getFullYear() - 1);
        this.selectedYear = defaultYear.getFullYear();
      });
    },

    perPage(size) {
      accountService
        .rememberPerPage('post', { from: 'report_excise_entry', perPage: size })
        .then((data) => {
          this.limit = data.size;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    /**
     * Show clients table data.
     */
    showClients(filter = {}) {
      this.loadingClients = true;
      reportService.clients(filter).then((clients) => {
        this.loadingClients = false;
        this.clientData = clients;
        this.clientData.forEach((item) => {
          this.dropdownState[item.id] = false;
          this.buttonLoading[item.id] = false;
        });
      });
    },

    /**
     * Get sales table data.
     */
    showSales() {
      this.loadingSales = true;
      reportService
        .sales(
          this.salesGrouping,
          this.salesDateRange,
          this.salesFilter,
          this.salesType
        )
        .then((report) => {
          this.loadingSales = false;
          this.salesData = report;
        });
    },
    aggFmtCurrency(row, col, value) {
      if (value === null || value === undefined || value === '') return '-';
      return Number(value).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    aggFmtVolume(row, col, value) {
      if (value === null || value === undefined || value === '') return '-';
      return Number(value).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    aggFmtInt(row, col, value) {
      if (value === null || value === undefined || value === '') return '-';
      return Number(value).toLocaleString('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },

    aggFmtQty(row, col, value) {
      if (value === null || value === undefined || value === '') return '-';
      const n = Number(value);
      const dec = n % 1 === 0 ? 0 : 2;
      return n.toLocaleString('nb-NO', { minimumFractionDigits: dec, maximumFractionDigits: dec });
    },

    aggFmtSumCurrency(value) {
      if (value === null || value === undefined) return '-';
      return Number(value).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    aggFmtSumVolume(value) {
      if (value === null || value === undefined) return '-';
      return Number(value).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    showAggSales() {
      if (!this.aggSalesDateRange[0] || !this.aggSalesDateRange[1]) return;
      this.loadingAggSales = true;
      reportService
        .aggregatedSales({
          groupBy: this.aggSalesGroupBy,
          dateRange: this.aggSalesDateRange,
          includeCancelled: this.aggSalesIncludeCancelled,
          sort: this.aggSalesSort,
          statuses: this.aggSalesStatuses.length ? this.aggSalesStatuses : null,
        })
        .then((result) => {
          this.loadingAggSales = false;
          this.aggSalesColumns = result.columns;
          this.aggSalesSummary = result.summary;
          this.aggSalesData = AggregatedSalesReportTransformer.fetchCollection(result.rows);
        })
        .catch(() => {
          this.loadingAggSales = false;
        });
    },

    onAggSalesFilterChange(name, value) {
      if (name === 'aggSalesDateRange' && value === undefined) {
        this.aggSalesDateRange = [moment().subtract(1, 'months'), moment()];
      } else {
        this[name] = value;
      }
      this.showAggSales();
    },

    downloadAggSalesReport() {
      const params = {};
      if (this.aggSalesDateRange[0] && this.aggSalesDateRange[1]) {
        params.start = moment(this.aggSalesDateRange[0]).format('YYYY-MM-DD');
        params.end = moment(this.aggSalesDateRange[1]).format('YYYY-MM-DD');
      }
      (this.aggSalesGroupBy || []).forEach((g, i) => {
        params[`group_by[${i}]`] = g;
      });
      params.sort = this.aggSalesSort;
      if (this.aggSalesIncludeCancelled) {
        params.include_cancelled = '1';
      }
      if (this.aggSalesStatuses.length) {
        this.aggSalesStatuses.forEach((s, i) => {
          params[`statuses[${i}]`] = s;
        });
      }
      const metricKeyMap = {
        totalRevenueNet: 'total_revenue_net',
        totalRevenueGross: 'total_revenue_gross',
        totalVolumeLiters: 'total_volume_liters',
        orderCount: 'order_count',
        totalQuantityUnits: 'total_quantity_units',
      };
      this.aggSalesMetrics.forEach((m, i) => {
        params[`metrics[${i}]`] = metricKeyMap[m] || m;
      });
      params.locale = this.$i18n.locale;
      window.open(
        helpers.buildUrl(`${process.env.API_LOCATION}/reports/aggregated_sales/export`, {
          token: localStorage.getItem('id_token'),
          ...params,
        })
      );
    },

    showExcise: debounce(function (page = 1, limit = 10) {
      this.loadingExcise = true;
      reportService
        .excisePeriods({
          page,
          limit,
        })
        .then(() => {
          this.loadingExcise = false;
        })
        .catch(() => {
          this.loadingExcise = false;
        });
    }, 800),

    exciseStatusClass(status) {
      return {
        'bg-info': status === 'Ongoing',
        'bg-success': status === 'Ready to be closed',
        'bg-default': status === 'Closed',
        'bg-warning': status === 'Changed',
      };
    },
    /**
     * Go to excise report view.
     *
     * @param  {Object} excisePeriod object
     */
    viewExciseMonthlyReport(excisePeriod) {
      this.$router.push({
        name: 'exciseMonthlyReport',
        params: {
          id: excisePeriod.id,
        },
      });
    },
    /**
     * Get bills table data.
     */
    showBills() {
      this.loadingBills = true;
      reportService
        .bills(this.billsDateRange, this.billClientId)
        .then((report) => {
          this.loadingBills = false;
          this.billData = report;
        });
    },

    /**
     * Get fees table data.
     */
    showFees() {
      this.loadingFees = true;
      reportService.fees(this.feesMonth, this.client).then((report) => {
        this.loadingFees = false;
        this.feeData = report;
        this.feeReportLoaded = true;
      });
    },

    /**
     * Get fees table data.
     */
    showWarehouses() {
      this.warehouseLoading = true;
      this.warehouseTransactionData = [];
      this.warehouseData = [];
      const params = {};
      params.warehouseId = this.warehouse;
      params.transactionTypeId = this.transaction_type_id;
      params.transactionDateRange = this.transactionDateRange;
      params.asOfDate = this.asOfDate;
      params.dateOption = this.dateOption;
      params.groupByProduct = this.groupByProduct;
      params.hideOutOfStock = this.hideOutOfStock;
      reportService.warehouseTransactions(params).then((report) => {
        this.warehouseLoading = false;
        if (!this.groupByProduct) {
          this.warehouseTransactionData = report;
        } else {
          this.warehouseData = report;
        }
      });
    },

    onTransactionDateRangeChange(name, value) {
      this[name] = value;
    },

    onFilterChangeDebounce: debounce(function (name, value) {
      this.onFilterChange(name, value);
    }, 800),
    /**
     * Listen to changes on form inputs.
     *
     * @param {String} name The input name.
     * @param {String} value The input value.
     */
    onFilterChange(name, value) {
      if (
        (name === 'billsDateRange' ||
          name === 'salesDateRange' ||
          name === 'feesDateRange' ||
          name === 'exciseDateRange') &&
        value === undefined
      ) {
        this[name] = [moment().subtract(1, 'months'), moment()];
      } else {
        this[name] = value;
      }

      if (name === 'billsDateRange') {
        this.showBills();
        return;
      }
      if (name === 'feesMonth') {
        if (this.feesMonth != null) {
          this.showFees();
        } else {
          this.feeData = [];
          this.feeReportLoaded = false;
        }
        return;
      }

      this.showSales();
    },

    /**
     * Downloads the reports.
     */
    downloadBillReport() {
      const params = {};

      if (this.rowLimit > 0) {
        params.limit = this.rowLimit;
      }

      if (this.billClientId != null) {
        params.client_id = this.billClientId;
      }

      if (this.billsDateRange[0] && this.billsDateRange[1]) {
        params.start = moment(this.billsDateRange[0]).format('YYYY-MM-DD');
        params.end = moment(this.billsDateRange[1]).format('YYYY-MM-DD');
      }

      window.open(
        helpers.buildUrl(`${process.env.API_LOCATION}/reports/bills/export/`, {
          token: localStorage.getItem('id_token'),
          ...params,
        })
      );
    },

    link(bill) {
      const name =
        bill.billTypeName !== 'Credit Note' ? 'bills.edit' : 'bills.creditNote';
      const routeData = this.$router.resolve({
        params: { id: bill.id },
        name,
      });
      window.open(routeData.href, '_blank');
    },

    querySearch(queryString, cb) {
      const options = this.clientData.map((o) => {
        o.value = o.name.toString().toLowerCase();
        return o;
      });
      const createFilter = option =>
        option.value.indexOf(queryString.toLowerCase()) === 0;
      const results = queryString ? options.filter(createFilter) : options;
      cb(results);
    },
    handleSelect(item) {
      this.client_id = item;
      this.showFees();
    },
    handleSelectWarehouse(item) {
      this.warehouse_id = item;
      this.showWarehouses();
    },
    handleSelectTransaction(item) {
      this.transaction_id = item;
      // this.showWarehouses();
    },
    /**
     * Downloads the reports.
     */
    downloadFeesReport() {
      const params = {};

      if (this.rowLimit > 0) {
        params.limit = this.rowLimit;
      }

      if (this.client != null) {
        params.client = this.client;
      }
      if (this.feesDateRange[0] && this.feesDateRange[1]) {
        params.start = moment(this.feesDateRange[0]).format('YYYY-MM-DD');
        params.end = moment(this.feesDateRange[1]).format('YYYY-MM-DD');
      }

      window.open(
        helpers.buildUrl(`${process.env.API_LOCATION}/reports/fees/export/`, {
          token: localStorage.getItem('id_token'),
          ...params,
        })
      );
    },

    downloadSalesReport() {
      const params = {};

      if (this.rowLimit > 0) {
        params.limit = this.rowLimit;
      }

      if (this.client != null) {
        params.client = this.client;
      }
      if (this.salesDateRange[0] && this.salesDateRange[1]) {
        params.start = moment(this.salesDateRange[0]).format('YYYY-MM-DD');
        params.end = moment(this.salesDateRange[1]).format('YYYY-MM-DD');
      }
      params.filter = this.salesFilter;
      params.group = this.salesGrouping;
      params.type = this.salesType;

      window.open(
        helpers.buildUrl(`${process.env.API_LOCATION}/reports/sales/export/`, {
          token: localStorage.getItem('id_token'),
          ...params,
        })
      );
    },
    downloadYtdSalesReport() {
      const params = {};

      if (this.salesDateRange[0] && this.salesDateRange[1]) {
        params.start = moment(this.salesDateRange[0]).format('YYYY-MM-DD');
        params.end = moment(this.salesDateRange[1]).format('YYYY-MM-DD');
      }
      if (this.salesFilter) {
        params.filter = this.salesFilter;
      }
      if (this.salesType) {
        params.type = this.salesType;
      }

      window.open(
        helpers.buildUrl(`${process.env.API_LOCATION}/reports/sales/export-ytd`, {
          token: localStorage.getItem('id_token'),
          ...params,
        })
      );
    },
    /**
     * Downloads the reports.
     */
    downloadWarehousesReport() {
      const params = {};

      if (this.rowLimit > 0) {
        params.limit = this.rowLimit;
      }

      if (this.client != null) {
        params.warehouse_id = this.warehouse;
      }

      if (this.transaction_type_id) {
        params.transaction_type_id = this.transaction_type_id;
      }

      if (this.dateOption === 'as_of_date') {
        if (this.asOfDate) {
          params.asOfDate = moment(this.asOfDate).format('YYYY-MM-DD');
        }

        if (this.hideOutOfStock) {
          params.hide_out_of_stock = this.hideOutOfStock;
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (this.transactionDateRange.length) {
          params.start = moment(this.transactionDateRange[0]).format(
            'YYYY-MM-DD'
          );
          params.end = moment(this.transactionDateRange[1]).format('YYYY-MM-DD');
        }
      }

      if (this.groupByProduct) {
        params.group_by_product = this.groupByProduct;
      }
      const url = helpers.buildUrl(`${process.env.API_LOCATION}/reports/warehouses_transactions/export/`,
        {
          token: localStorage.getItem('id_token'),
          ...params,
        });
      window.open(url);
    },

    /**
     * Downloads the reports.
     */
    downloadWarehousesReportBeta() {
      const params = {};

      if (this.rowLimit > 0) {
        params.limit = this.rowLimit;
      }

      if (!this.warehouse) {
        this.$message({
          showClose: true,
          message: this.$t('static.reports.warehouse.warehouse_required'),
          customClass: 'error',
        });
        return;
      }

      if (!this.transactionMonth) {
        this.$message({
          showClose: true,
          message: this.$t('static.reports.warehouse.month_required'),
          customClass: 'error',
        });
        return;
      }

      if (this.client != null) {
        params.warehouse_id = this.warehouse;
      }

      params.month = moment(this.transactionMonth).format(
        'YYYY-MM-DD'
      );
      params.monthly = 1;

      window.open(
        helpers.buildUrl(
          `${process.env.API_LOCATION}/reports/warehouses_transactions/export/`,
          {
            token: localStorage.getItem('id_token'),
            ...params,
          }
        )
      );
    },
    link(item, key) {
      let id = 0;
      let name = '';
      switch (key) {
        case 'orderNumber':
          id = item.orderId;
          name = 'orders.edit';
          break;
        case 'client':
          id = item.clientId;
          name = 'clients.edit';
          break;
        case 'productName':
          id = item.productId;
          name = 'products.edit';
          break;
        case 'number':
          id = item.order_id;
          name = 'orders.edit';
          break;
        case 'order_number':
          id = item.order_id;
          name = 'orders.edit';
          break;
        case 'product_name':
          id = item.product_id;
          name = 'products.edit';
          break;
        case 'order_id':
          id = item.order_id;
          name = 'orders.edit';
          break;
      }
      const routeData = this.$router.resolve({
        params: { id },
        name,
      });
      window.open(routeData.href, '_blank');
    },
    camalize(str) {
      return str
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
    },
  },

  components: {
    VTable: require('./../../../components/table/table.vue'),
    VDateRange: require('./../../../components/form/dateRange/dateRange.vue'),
    VMonth: require('./../../../components/form/month/month.vue'),
    VSelect: require('./../../../components/form/select/select.vue'),
    VText: require('./../../../components/form/text/text.vue'),
    VCard: require('./../../../components/card/card.vue'),
    VPagination: require('./../../../components/paginationNew/paginationNew.vue'),
    VNoData: require('./../../../components/noData/noData.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/reports/reports.js